import React, { useCallback, useContext, useEffect, useState } from 'react';
import type { MembershipSliceProps } from '@ww-digital/web-palette-react/dist/components/Slice/MembershipSlice/MembershipSlice';
import type { MembershipCardProps } from '@ww-digital/web-palette-react/dist/components/Price/MembershipCard/MembershipCard';
import { MembershipPlanPriceProps } from '@ww-digital/web-palette-react/dist/components/Price/MembershipPlan/MembershipPlan';
import { MembershipSlice } from '@ww-digital/web-palette-react/dist/components/Slice/MembershipSlice/MembershipSlice';
import type {
  OfferBundle,
  OfferBundleOfferPlan,
} from '../../../hooks/useProducts';
import { useProducts } from '../../../hooks/useProducts.ts';
import { usePostalCodeAndLocation } from '../../../hooks/usePostalCodeAndLocation.ts';
import { useZipOwner } from '../../../hooks/useZipOwner.ts';
import {
  ConfigContext,
  ConfigContextType,
} from '../../../context/config.context.ts';
import { PricingUtility } from '../../Utility/PricingUtility.ts';
import {
  MarketContext,
  MarketContextType,
} from '../../../context/market.context.ts';
import {
  getContentAsDictionary,
  getContentByKey,
  getContentObjectByKey,
  getPlanPrice,
} from '../PlansSliceContainer/PlanSliceContainerUtil.ts';
import { ProductsUtility } from '../../Utility/ProductsUtility.ts';
import {
  QueryStringContext,
  QueryStringContextType,
} from '../../../context/querystring.context.ts';
import {
  EntitlementContext,
  EntitlementContextType,
} from '../../../context/entitlement.context.ts';
import { cloneDeep } from 'lodash';

type MembershipPlanDataType = {
  subscriptionType: string;
  offerBundleId: string;
};

export type MembershipSliceExtendedProps = MembershipSliceProps & {
  plansData?: MembershipPlanDataType[];
  zipText?: string;
};

interface MembershipSliceContainerProps {
  daCategory: string;
  slice: MembershipSliceExtendedProps;
}

export const MembershipSliceContainer = ({
  slice,
}: MembershipSliceContainerProps): JSX.Element => {
  const sliceView = cloneDeep(slice);
  const [sliceState, setSliceState] = useState(sliceView);
  const queryArgs = useContext<QueryStringContextType>(QueryStringContext);
  const [currentOwnerId, setCurrentOwnerId] = useState(queryArgs.own || '37');
  const marketContext = useContext<MarketContextType>(MarketContext);
  const { translations } = useContext<ConfigContextType>(ConfigContext);
  const { entitlement } =
    useContext<EntitlementContextType>(EntitlementContext);
  const [currentPostalCode, setCurrentPostalCode] = useState('');
  const {
    loading: locationLoading,
    postalCode,
    updateUserPostalCode,
  } = usePostalCodeAndLocation();

  useEffect(() => {
    if (postalCode && !locationLoading) {
      setCurrentPostalCode(postalCode);
    }
  }, [postalCode, locationLoading]);

  const { ownerId, loading: zipOwnerLoading } = useZipOwner(postalCode);

  useEffect(() => {
    if (!zipOwnerLoading && ownerId && !queryArgs.own) {
      setCurrentOwnerId(ownerId);
    }
  }, [zipOwnerLoading, ownerId]);

  const getSlicePricing = () => {
    const slicePricing = {
      input: {
        buttonText: translations.PRICING_INPUT_BUTTON_TEXT,
        onSubmit: async (newPostalCode: string) => {
          setCurrentPostalCode(newPostalCode);
          await updateUserPostalCode({
            variables: { postalCode: newPostalCode },
          });
        },
        placeholder: translations.PRICING_INPUT_PLACEHOLDER,
        regex: PricingUtility.zipcodeRegExp(marketContext.country),
        regexErrorMessage: translations.PRICING_INPUT_ERROR,
        loading: locationLoading,
      },
      zipText: sliceView?.zipText || translations.PRICING_ZIP_TEXT,
      zipOrLocation: currentPostalCode,
    };

    return slicePricing;
  };

  const offerBundleInputs = queryArgs.ob
    ? [{ offerBundleId: queryArgs.ob }]
    : sliceView.plansData?.map((item: MembershipPlanDataType) => ({
        offerBundleId: item.offerBundleId || undefined,
        subscriptionType: item.offerBundleId
          ? undefined
          : item.subscriptionType,
      })) || [];

  const stg = !!queryArgs.sid && !!queryArgs.vid && queryArgs.ppm === 'stg';
  const { offerBundles: offerBundlesData, loading: offerBundlesLoading } =
    useProducts(offerBundleInputs, currentOwnerId, stg);

  const buildToutData = (bundle: OfferBundle) => {
    const subHeadingText = getContentByKey(bundle?.contents, 'DESCRIPTION');
    return {
      subheading: {
        text: subHeadingText,
        typog: 'bodycopy',
        variant: 'default',
        ariaLevel: 4,
      },
    };
  };

  const getFeaturesList = (bundle: OfferBundle) => {
    const features: { text: string; included: boolean }[] =
      getContentObjectByKey(bundle, 'FEATURES_DESCRIPTION');
    return features;
  };

  const buildFeatureItems = (bundle: OfferBundle) => {
    const features = getFeaturesList(bundle);

    return features?.map((item) => {
      return item?.text;
    });
  };

  const getFormattedPrice = (price?: number, removeDecimals = false) => {
    return ProductsUtility.formatCurrency(
      price || 0,
      marketContext.country,
      marketContext.language,
      removeDecimals,
    );
  };

  const buildCompareContents = (
    premiumBundle?: OfferBundle,
    coreBundle?: OfferBundle,
  ) => {
    if (!premiumBundle || !coreBundle) {
      return [];
    }
    const premiumFeatures = getFeaturesList(premiumBundle);
    const coreFeatures = getFeaturesList(coreBundle);

    return premiumFeatures.map((preItem) => {
      const coreFeatIndex = coreFeatures.findIndex(
        (core) => core.text === preItem.text,
      );
      let coreFeat = null;
      if (coreFeatIndex > -1) {
        coreFeat = coreFeatures[coreFeatIndex];
        coreFeatures.splice(coreFeatIndex, 1);
      }

      if (!coreFeat) {
        return {
          text: preItem.text,
          item1Type: 'notIncluded',
          item2Type: 'included',
        };
      }
      return {
        text: preItem.text,
        item1Type: 'grayIncluded',
        item2Type: 'included',
      };
    });
  };

  const replaceData = (text?: string, month?: string, value?: string) => {
    return (
      text?.replace('$MONTH', month || '').replace('$VALUE', value || '') || ''
    );
  };

  const getFreeMonthData = (planData?: OfferBundleOfferPlan) => {
    const plan = planData?.offerPlanData?.plan;
    const planRamps = plan?.planRamps;
    let discountPeriod = 0;
    let discountPrice = 0;
    if (planRamps && planRamps.length >= 2) {
      const rampStart = planRamps[0];
      const rampChange = planRamps[1];
      const startPrice = rampStart?.pricings
        ? parseInt(rampStart?.pricings[0]?.salePrice)
        : NaN;
      const endPrice = rampChange?.pricings
        ? parseInt(rampChange?.pricings[0]?.salePrice)
        : NaN;

      if (startPrice < endPrice) {
        discountPrice = Math.abs(startPrice - endPrice);
        discountPeriod =
          rampChange?.startingBillingSequence -
          rampStart?.startingBillingSequence;
      }
    }
    return {
      freeMonthAmount: discountPeriod,
      discount: discountPeriod * discountPrice,
    };
  };

  const buildPriceDetail = (
    priceProps: MembershipPlanPriceProps,
    defaultDataIndex: number,
    freeMonth: number,
    label: string,
    oldPrice: number | string | undefined,
    newPrice: number | string | undefined,
    discountValue?: string,
  ) => {
    const defaultData = priceProps?.priceDetails
      ? priceProps.priceDetails[defaultDataIndex] || {}
      : {};
    defaultData.label = replaceData(
      defaultData.label,
      `${freeMonth}`,
      discountValue,
    );
    defaultData.label2 = replaceData(
      defaultData.label2,
      `${freeMonth}`,
      discountValue,
    );
    defaultData.label3 = replaceData(
      defaultData.label3,
      `${freeMonth}`,
      discountValue,
    );
    const priceData = {
      ...{
        label: label,
        newPrice:
          newPrice === 0
            ? 'Free'
            : typeof newPrice === 'string'
              ? newPrice
              : getFormattedPrice(newPrice),
        price:
          typeof oldPrice === 'string' ? oldPrice : getFormattedPrice(oldPrice),
      },
      ...defaultData,
    };
    return priceData;
  };

  const getPriceData = (
    bundlePlan?: OfferBundleOfferPlan,
    priceProps?: MembershipPlanPriceProps,
    bundle?: OfferBundle,
    isBestPlan?: boolean,
  ): MembershipPlanPriceProps => {
    if (!bundlePlan?.offerPlanData || !bundle) {
      return {};
    }

    priceProps = priceProps || {};

    const price = getFormattedPrice(
      bundlePlan?.offerPlanData?.plan.salePrice,
      true,
    );

    const buttonText = replaceData(
      priceProps?.button?.text,
      `${bundlePlan?.offerPlanData?.plan?.basePlan?.totalBillingCycles}`,
    );

    const secondaryButtonText = replaceData(
      priceProps?.button?.secondaryText,
      `${bundlePlan?.offerPlanData?.plan?.basePlan?.totalBillingCycles}`,
    );

    const priceValue = getPlanPrice(
      queryArgs,
      price,
      entitlement,
      bundlePlan,
      buttonText,
      secondaryButtonText,
      bundle,
    );

    priceValue.priceText = priceProps?.priceText || priceValue.priceText;

    const contentsDic = getContentAsDictionary(
      bundlePlan?.offerPlanData?.contents,
    );

    const planContentsDic = getContentAsDictionary(
      bundlePlan?.offerPlanData?.plan.contents,
    );

    const totalLabel =
      priceProps?.priceTotal?.label || contentsDic['TOTAL_LABEL'];
    const offerTerms = priceProps?.offerTerms || contentsDic['DISCLAIMER'];
    const starterFeeLabel = contentsDic['STARTER_FEE_LABEL'];
    const discountLabel = contentsDic['DISCOUNT_LABEL'];
    const planLabel = contentsDic['SUBSCRIPTION_PLAN_DISPLAY_NAME'];
    const promoInfoText =
      priceProps?.promoInfoText || contentsDic['PROMOTION_CONTENT'];

    const starterFee = bundlePlan.offerPlanData?.addons.find(
      (add) =>
        add.state === 'active' &&
        add.addonItem.name.indexOf('Starter Fee') > -1,
    );

    const freeMonthData = getFreeMonthData(bundlePlan);

    const totalSavings =
      (starterFee?.retailPrice || 0) + freeMonthData.discount;
    const totalMonth =
      bundlePlan.offerPlanData?.plan?.basePlan?.totalBillingCycles;

    const promoText =
      replaceData(
        priceProps?.promoText,
        `${totalMonth}`,
        getFormattedPrice(totalSavings, true),
      ) || contentsDic['SPECIAL_OFFER'];

    const plan = bundlePlan.offerPlanData?.plan;

    const asteriskText =
      replaceData(
        priceProps?.asteriskText,
        `${totalMonth}`,
        `${getFormattedPrice(plan?.retailPrice, true)}/mo`,
      ) || planContentsDic['STARTER_FEE_SUBLABEL'];

    const formattedDiscount = getFormattedPrice(plan?.discount, true);

    const starterFeePrice = buildPriceDetail(
      priceProps,
      0,
      freeMonthData.freeMonthAmount,
      starterFeeLabel,
      starterFee?.retailPrice,
      starterFee?.salePrice,
      formattedDiscount,
    );

    const discountPrice = buildPriceDetail(
      priceProps,
      1,
      freeMonthData.freeMonthAmount,
      discountLabel,
      freeMonthData?.discount || plan?.retailPrice,
      plan?.salePrice,
      formattedDiscount,
    );

    const planPrice = buildPriceDetail(
      priceProps,
      2,
      freeMonthData.freeMonthAmount,
      planLabel,
      '',
      `${getFormattedPrice(plan?.retailPrice, true)}/mo`,
      formattedDiscount,
    );

    if (!planPrice?.detailsInfo) {
      planPrice.detailsInfo =
        bundlePlan?.offerPlanData?.plan?.templateContents?.find(
          (templ) => templ.templateContentKeyName === 'TERMS_AND_CONDITIONS',
        )?.templateContent?.templateValue || '';
    }

    const totalValue = getFormattedPrice(bundlePlan.offerPlanData?.total);
    const daLabel = priceValue?.button?.attributes
      ? priceValue?.button?.attributes['da-label']
      : '';
    return {
      promoText,
      promoInfoText,
      promoAttributes: {
        'da-action': 'click',
        'da-label': daLabel,
        'da-category': 'promo-info-button',
      },
      priceTotal: {
        label: totalLabel,
        newPrice: totalValue,
      },
      priceDetails: isBestPlan
        ? [starterFeePrice, discountPrice, planPrice]
        : [starterFeePrice, discountPrice],
      ...priceValue,
      asteriskText,
      offerTerms,
      offerTermsAttribute: {
        'da-action': 'click',
        'da-label': daLabel,
        'da-category': 'offer-terms',
      },
      offerTermsText: priceProps?.offerTermsText,
    };
  };

  const alterSliceData = (
    sliceCard?: MembershipCardProps,
    bundle?: OfferBundle,
    hasPricing?: boolean,
  ) => {
    if (!sliceCard || !bundle) {
      return;
    }

    sliceCard.tout = {
      ...buildToutData(bundle),
      ...sliceCard.tout,
    };
    sliceCard.featureItems = buildFeatureItems(bundle);

    const bestPlanData: OfferBundleOfferPlan =
      bundle.offerPlans.find(
        (b) => b.offerPlanId === bundle?.bestOfferPlanId,
      ) || bundle.offerPlans[0];

    const defaultBestPriceProps = sliceCard?.planProps?.plans
      ? sliceCard.planProps?.plans[0]
      : {};

    const secondOfferData: OfferBundleOfferPlan =
      bundle?.offerPlans?.find(
        (plan) => plan.offerPlanId !== bestPlanData?.offerPlanId,
      ) || ({} as OfferBundleOfferPlan);

    const defaultSecondPriceProps = sliceCard?.planProps?.plans
      ? sliceCard.planProps?.plans[1]
      : {};

    const bestPriceProps = getPriceData(
      bestPlanData,
      defaultBestPriceProps,
      bundle,
      true,
    );

    const secondPriceProps = getPriceData(
      secondOfferData,
      defaultSecondPriceProps,
      bundle,
    );

    sliceCard.planProps = {
      ...sliceCard.planProps,
      plans: [bestPriceProps, secondPriceProps],
    };

    if (hasPricing) {
      sliceCard.tout.pricing = getSlicePricing();
    }
  };

  const initializeBundles = useCallback(() => {
    if (offerBundlesLoading || !offerBundlesData?.length) {
      return;
    }

    const offerBundles = offerBundlesData;

    const premiumCard = sliceView.memberShipChoice?.first
      ?.card as MembershipCardProps;
    const coreCard = sliceView.memberShipChoice?.second
      ?.card as MembershipCardProps;

    const premiumBundle = offerBundles.find(
      (offer) => offer.subscriptionType === 'PREMIUM',
    );
    const coreBundle = offerBundles.find(
      (offer) => offer.subscriptionType === 'CORE',
    );

    alterSliceData(coreCard, coreBundle, false);
    alterSliceData(premiumCard, premiumBundle, true);

    sliceView.compareData.contents = buildCompareContents(
      premiumBundle,
      coreBundle,
    );
    setSliceState(sliceView);
  }, [
    offerBundlesLoading,
    offerBundlesData,
    currentOwnerId,
    currentPostalCode,
    queryArgs.op,
    queryArgs.st,
    queryArgs.ma,
  ]);

  useEffect(() => {
    initializeBundles();
  }, [offerBundlesLoading, currentOwnerId, currentPostalCode]);

  const sliceContainer = <MembershipSlice {...sliceState} />;

  return <div>{sliceContainer}</div>;
};
